import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { generateTextStyles } from '../../utils/textStyles';
import { ensureGoogleFont } from '../ThemeApplier';

const Device = createContext('desktop');
const Block = createContext({});
const currentDevice = () => window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
export const ResponsiveTypography = ({ children }) => {
  const [device, setDevice] = useState(currentDevice);
  useEffect(() => { const resize = () => setDevice(currentDevice()); window.addEventListener('resize', resize); return () => window.removeEventListener('resize', resize); }, []);
  return <Device.Provider value={device}>{children}</Device.Provider>;
};
export const BlockTypography = ({ block, children }) => {
  useEffect(() => { Object.entries(block.content?.textStyles || {}).forEach(([key, value]) => { if (key.endsWith('Font')) ensureGoogleFont(value); }); }, [block.content?.textStyles]);
  return <Block.Provider value={block}>{children}</Block.Provider>;
};
export const useFieldStyle = (field, ownStyles) => {
  const block = useContext(Block);
  const device = useContext(Device);
  const style = generateTextStyles(ownStyles || block.content?.textStyles || {}, field, device);
  if (block.styleMode === 'individual') {
    const color = field === 'title' ? block.titleColor : field === 'subtitle' ? block.subtitleColor : field === 'description' ? block.contentColor : null;
    if (color) style.color = color;
  }
  Object.entries(style).forEach(([key, value]) => { style[`--cms-${key.replace(/[A-Z]/g, m => '-' + m.toLowerCase())}`] = value; });
  return style;
};

const permittedCss = new Set(['color','background-color','font-family','font-size','font-weight','font-style','text-decoration','text-align','line-height']);
export const RichText = ({ value, as: Tag = 'span', field, className = '', testId, children, allowLinks = true, ...rest }) => {
  const styles = useFieldStyle(field);
  const html = useMemo(() => {
    const raw = typeof value === 'object' ? value?.text || value?.title || '' : String(value || '');
    const fragment = DOMPurify.sanitize(raw, { RETURN_DOM_FRAGMENT: true, ALLOWED_TAGS: ['p','br','strong','b','em','i','u','s','span','a','ul','ol','li','blockquote'], ALLOWED_ATTR: ['style','href','target','rel'] });
    fragment.querySelectorAll('[style]').forEach(el => {
      Array.from(el.style).forEach(property => {
        const camel = property.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        if (!permittedCss.has(property) || styles[camel] !== undefined) el.style.removeProperty(property);
      });
    });
    fragment.querySelectorAll('a').forEach((a, i) => {
      if (!allowLinks) { a.replaceWith(...a.childNodes); return; }
      a.setAttribute('data-testid', `${testId}-inline-link-${i}`);
      if (a.getAttribute('target') === '_blank') a.setAttribute('rel', 'noopener noreferrer');
    });
    if (['span','h1','h2','h3','summary','p'].includes(Tag)) {
      fragment.querySelectorAll('p,ul,ol,li,blockquote').forEach(el => { const span = document.createElement('span'); span.innerHTML = el.innerHTML; span.style.cssText = el.style.cssText; el.replaceWith(span, document.createElement('br')); });
      while (fragment.lastChild?.nodeName === 'BR') fragment.lastChild.remove();
    }
    const container = document.createElement('div'); container.appendChild(fragment);
    return container.innerHTML;
  }, [value, Tag, allowLinks, testId, JSON.stringify(styles)]);
  const props = { className: `tdp-rich ${className}`, style: styles, 'data-testid': testId, ...rest };
  return children ? <Tag {...props}><span dangerouslySetInnerHTML={{ __html: html }} />{children}</Tag> : <Tag {...props} dangerouslySetInnerHTML={{ __html: html }} />;
};